import { Component, OnInit, Input } from "@angular/core";
import { InvoiceService } from "../../service/invoice.service";
import { HttpErrorResponse } from "@angular/common/http";
import { Message } from "primeng/components/common/api";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";

@Component({
  selector: "app-invoice-file-preview",
  templateUrl: "./invoice-file-preview.component.html",
  styleUrls: ["./invoice-file-preview.component.css"],
})
export class InvoiceFilePreviewComponent implements OnInit {
  @Input() InvoiceId?: number;
  @Input() InvoiceLink: string;
  @Input() RawFileName: string;

  invoiceFile: { FileUrl?: string; FileName?: string; InvoiceId?: number } = {};
  showDownloadButton: boolean = false;
  showDownloadWarning: boolean = false;
  msgs: Message[] = [];
  actualInvoiceId: number;

  pdfBlobUrl: SafeResourceUrl;
  isLoading: boolean = true;

  constructor(
    private invoiceService: InvoiceService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit() {
    console.log("Component initialized with InvoiceId:", this.InvoiceId);
    console.log("InvoiceLink:", this.InvoiceLink);
    this.getInvoiceFile();
  }

  getInvoiceFile() {
    const invoiceFilter = {
      InvoiceIdLink: this.InvoiceLink,
      InvoiceId: this.InvoiceId,
    };

    this.invoiceService.getInvoiceFileStream(invoiceFilter).subscribe(
      (result: any) => {
        this.invoiceFile = result || {};
        this.showDownloadButton = true;
        this.showDownloadWarning = !this.invoiceFile.FileUrl;

        if (!this.InvoiceId && this.invoiceFile.FileUrl) {
          const urlParts = this.invoiceFile.FileUrl.split("/");
          this.actualInvoiceId = parseInt(urlParts[urlParts.length - 1]);
        } else {
          this.actualInvoiceId = this.InvoiceId;
        }

        console.log("Invoice File loaded:", this.invoiceFile);
        console.log("Actual Invoice ID:", this.actualInvoiceId);

        this.loadPdfPreview();
      },
      (err: HttpErrorResponse) => {
        this.invoiceFile = {};
        this.showDownloadButton = true;
        this.showDownloadWarning = true;
        this.isLoading = false;
        this.showMessage("error", "", "תקלה בקבלת נתוני קובץ בחשבונית");
      }
    );
  }

  loadPdfPreview() {
    if (!this.actualInvoiceId) {
      this.isLoading = false;
      return;
    }

    this.invoiceService.downloadInvoiceFile(this.actualInvoiceId).subscribe(
      (blob: Blob) => {
        console.log("Preview blob received:", {
          size: blob.size,
          type: blob.type,
        });

        // Check if blob is valid
        if (blob.size === 0) {
          console.error("Received empty blob");
          this.isLoading = false;
          this.showMessage("error", "", "הקובץ ריק");
          return;
        }

        // Check if we received an error response instead of a file
        if (
          blob.type === "application/json" ||
          blob.type === "text/xml" ||
          blob.type === "text/html"
        ) {
          console.error(
            "Received error response instead of file, type:",
            blob.type
          );
          this.isLoading = false;

          // Try to read the error message
          const reader = new FileReader();
          reader.onload = () => {
            console.error("Error response content:", reader.result);
          };
          reader.readAsText(blob);

          this.showMessage("error", "", "שגיאה בקבלת הקובץ מהשרת");
          return;
        }

        const url = window.URL.createObjectURL(blob);
        this.pdfBlobUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        this.isLoading = false;
      },
      (err) => {
        console.error("Failed to load PDF preview", err);
        this.isLoading = false;
        this.showMessage("error", "", "תקלה בטעינת התצוגה המקדימה");
      }
    );
  }

  openInvoiceInNewTab() {
    if (!this.actualInvoiceId) {
      this.showMessage("error", "", "חסר מזהה חשבונית");
      return;
    }

    this.invoiceService.downloadInvoiceFile(this.actualInvoiceId).subscribe(
      (blob: Blob) => {
        console.log("New tab blob received:", {
          size: blob.size,
          type: blob.type,
        });

        if (
          blob.size === 0 ||
          blob.type === "application/json" ||
          blob.type === "text/xml"
        ) {
          this.showMessage("error", "", "שגיאה בקבלת הקובץ");
          return;
        }

        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 100);
      },
      (err) => {
        console.error("Open in new tab failed", err);
        this.showMessage("error", "", "תקלה בפתיחת הקובץ");
      }
    );
  }

  downloadInvoiceFile() {
    const originalFileName =
      this.invoiceFile && this.invoiceFile.FileName
        ? this.invoiceFile.FileName
        : this.RawFileName || "invoice";

    if (!this.actualInvoiceId) {
      this.showMessage("error", "", "חסר מזהה חשבונית");
      return;
    }

    console.log("Starting download for invoice ID:", this.actualInvoiceId);

    this.invoiceService.downloadInvoiceFile(this.actualInvoiceId).subscribe(
      (blob: Blob) => {
        console.log("Download blob received:", {
          size: blob.size,
          type: blob.type,
          originalFileName: originalFileName,
        });

        // Validate blob
        if (blob.size === 0) {
          console.error("Received empty blob for download");
          this.showMessage("error", "", "הקובץ ריק");
          return;
        }

        // Check if we received an error instead of a file
        if (
          blob.type === "application/json" ||
          blob.type === "text/xml" ||
          blob.type === "text/html"
        ) {
          console.error("Received error response instead of file");
          this.showMessage("error", "", "שגיאה בהורדת הקובץ");
          return;
        }

        // Fix the filename extension based on actual file type
        let fileName = originalFileName;
        const actualExtension = this.getExtensionFromMimeType(blob.type);

        if (actualExtension) {
          // Remove existing extension and add correct one
          const nameWithoutExt = fileName.replace(/\.[^/.]+$/, "");
          fileName = nameWithoutExt + actualExtension;
          console.log("Corrected filename:", fileName);
        }

        const a = document.createElement("a");
        const objectUrl = URL.createObjectURL(blob);
        a.href = objectUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        setTimeout(() => {
          URL.revokeObjectURL(objectUrl);
        }, 100);

        console.log("Download triggered successfully with filename:", fileName);
      },
      (err) => {
        console.error("Download failed with error:", err);
        this.showMessage("error", "", "תקלה בהורדת הקובץ");
      }
    );
  }

  /** Helper to get correct file extension from MIME type */
  getExtensionFromMimeType(mimeType: string): string {
    const mimeToExt = {
      "application/pdf": ".pdf",
      "image/jpeg": ".jpg",
      "image/jpg": ".jpg",
      "image/png": ".png",
      "image/gif": ".gif",
      "image/bmp": ".bmp",
      "image/tiff": ".tiff",
      "application/msword": ".doc",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        ".docx",
      "application/vnd.ms-excel": ".xls",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        ".xlsx",
      "text/plain": ".txt",
      "text/html": ".html",
      "application/zip": ".zip",
    };

    return mimeToExt[mimeType] || "";
  }

  showMessage(type: string, summary: string, detail: string) {
    this.msgs = [];
    this.msgs.push({ severity: type, summary: summary, detail: detail });
  }
}



// WEBPACK FOOTER //
// C:/MyProjects_Git/GoDocu/GoDocuMng/Client/src/app/invoice/invoice-file-preview/invoice-file-preview.component.ts